import { apiSlice } from './apiSlice';
import { decodeToken } from '../../utils/TokenUtils';

export const userApi = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getAllUsers: builder.query({
			query: (params) => {
				if (params && typeof params === 'object') {
					return {
						url: `/api/v1/users`,
						params,
					};
				}
				return `/api/v1/users`;
			},
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				if (token) {
					const decoded = decodeToken(token);
					return decoded;
				}
				const data = response?.data?.data ?? response?.data ?? response?.message;
				if (data && typeof data === 'object' && !Array.isArray(data)) {
					if (data.users && Array.isArray(data.users)) {
						return data;
					}
				}
				if (Array.isArray(data)) return data;
				if (Array.isArray(response)) return response;
				return [];
			},
			providesTags: ['User'],
		}),
		getUserPatients: builder.query({
			query: (userId) => `/api/v1/users/patients/${userId}`,
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				if (token) {
					const decoded = decodeToken(token);
					return Array.isArray(decoded) ? decoded : [];
				}
				const data = response?.data?.data ?? response?.data ?? response?.message;
				if (Array.isArray(data)) return data;
				return [];
			},
			providesTags: (result, error, id) => [{ type: 'User' as const, id }, 'User'],
		}),
		getUserCenters: builder.query({
			query: (userId) => `/api/v1/users/centers/${userId}`,
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				if (token) {
					const decoded = decodeToken(token);
					return Array.isArray(decoded) ? decoded : [];
				}
				const data = response?.data?.data ?? response?.data ?? response?.message;
				if (Array.isArray(data)) return data;
				return [];
			},
			providesTags: (result, error, id) => [{ type: 'User' as const, id }, 'User'],
		}),
		createUser: builder.mutation({
			query: (data) => {
				const { selectCenter, ...payload } = data;
				if (selectCenter) {
					payload.centerId = selectCenter.centerId;
					return {
						url: `/api/v1/users/centers-patients`,
						method: 'POST',
						body: payload,
					};
				}
				return {
					url: `/api/v1/users`,
					method: 'POST',
					body: payload,
				};
			},
			invalidatesTags: ['User'],
		}),
		updateUser: builder.mutation({
			query: (data) => {
				const { userId, role, centerId, ...payload } = data;
				// Real codebase sends a PATCH to users, and a POST to assignToUser
				return {
					url: `/api/v1/users/${userId}`,
					method: 'PATCH',
					body: payload,
				};
			},
			// Invalidate user tags so list refreshes
			invalidatesTags: (result, error, arg) => [{ type: 'User' as const, id: arg.userId }, 'User'],
		}),
		deleteUser: builder.mutation({
			query: (userId) => ({
				url: `/api/v1/users/${userId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['User'],
		}),
		deleteUserCompletely: builder.mutation({
			query: (userId) => ({
				url: `/api/v1/users/full-delete/${userId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['User'],
		}),
		assignCenterAndPatients: builder.mutation({
			query: (data) => ({
				url: `/api/v1/user/assignToUser/${data.userId}`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['User'],
		}),
		unassignPatient: builder.mutation({
			query: ({ userId, patientId }) => ({
				url: `/api/v1/users/patients/${userId}`,
				method: 'DELETE',
				body: { patientId },
			}),
			invalidatesTags: (result, error, { userId }) => [{ type: 'User' as const, id: userId }, 'User'],
		}),
		unassignCenter: builder.mutation({
			query: ({ userId, centerId }) => ({
				url: `/api/v1/users/centers/${userId}`,
				method: 'DELETE',
				body: { centerId },
			}),
			invalidatesTags: (result, error, { userId }) => [{ type: 'User' as const, id: userId }, 'User'],
		}),
		forgotPassword: builder.mutation({
			query: (email) => ({
				url: `/api/v1/user/forgotpassword`,
				method: 'POST',
				body: { email },
			}),
		}),
		verifyCode: builder.mutation({
			query: (code) => ({
				url: `/api/v1/user/send/newpassword`,
				method: 'POST',
				body: { code },
			}),
		}),
	}),
});

export const {
	useGetAllUsersQuery,
	useGetUserPatientsQuery,
	useGetUserCentersQuery,
	useCreateUserMutation,
	useUpdateUserMutation,
	useDeleteUserMutation,
	useDeleteUserCompletelyMutation,
	useAssignCenterAndPatientsMutation,
	useUnassignPatientMutation,
	useUnassignCenterMutation,
	useForgotPasswordMutation,
	useVerifyCodeMutation,
} = userApi;

export const useGetAllCenterByUserQuery = useGetUserCentersQuery;
export const useGetAllPatientsByUserQuery = useGetUserPatientsQuery;
